import React from "react"
import { useTranslation } from "react-i18next"

import { UserSearch } from "@/components/user-search"
import { useAuthentication } from "@/contexts"
import { useFormatPersonName } from "@/formatters"
import type { Organization } from "@/models/organization"
import type { User } from "@/models/user"
import type { UserRoleType } from "@/models/user-role"
import { DialogTitle } from "@/nzds/feedback/dialog-title/dialog-title"
import { ReadonlyField } from "@/nzds/forms/readonly-field/readonly-field"
import { useAddExistingUserToOrganization } from "@/services/user"
import { getDisplayValue } from "@/utils/value"

import Button from "@mui/material/Button"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import Fade from "@mui/material/Fade"
import Stack from "@mui/material/Stack"

interface AddExistingUserDialogProps {
  onClose: () => void
  onSuccess?: () => void
  open: boolean
  organizationId: Organization["id"]
}

export const AddExistingUserDialog: React.FC<AddExistingUserDialogProps> = ({
  onClose,
  onSuccess,
  open,
  organizationId,
}) => {
  const { t } = useTranslation("common")
  const { isOrgAdminUser } = useAuthentication()
  const { formatPersonName } = useFormatPersonName()
  const [selectedUser, setSelectedUser] = React.useState<User | null>(null)

  const roleTranslationKeyByRole: Record<UserRoleType, string> = {
    admin: "impersonateRole.role.admin.name",
    admin_reader: "impersonateRole.role.adminReader.name",
    consultant_user: "impersonateRole.role.nzeroOSUser.name",
    org_utility_credentials_admin:
      "impersonateRole.role.orgUtilityCredentialsAdmin.name",
    org_admin: "impersonateRole.role.orgAdmin.name",
  }

  const closeDialog = () => {
    setSelectedUser(null)
    onClose()
  }

  const selectedUserName = getDisplayValue(
    selectedUser
      ? formatPersonName({
          firstName: selectedUser.firstName,
          lastName: selectedUser.lastName,
        })
      : undefined
  )
  const selectedUserRoleTranslationKey = selectedUser?.role
    ? roleTranslationKeyByRole[selectedUser.role]
    : undefined
  const selectedUserRoleLabel = getDisplayValue(
    selectedUserRoleTranslationKey
      ? t(selectedUserRoleTranslationKey)
      : undefined
  )

  const {
    addExistingUserToOrganizationMutateAsync,
    addExistingUserToOrganizationIsPending,
  } = useAddExistingUserToOrganization({
    onSuccess: () => {
      closeDialog()
      onSuccess?.()
    },
  })

  const handleSubmit = async () => {
    if (!selectedUser?.id) {
      return
    }

    await addExistingUserToOrganizationMutateAsync({
      organizationId,
      userId: selectedUser.id,
    })
  }

  const handleClose = () => {
    if (!addExistingUserToOrganizationIsPending) {
      closeDialog()
    }
  }

  return (
    <Dialog
      aria-labelledby="add-existing-user-dialog-title"
      fullWidth
      maxWidth="sm"
      onClose={handleClose}
      open={open}
    >
      <DialogTitle id="add-existing-user-dialog-title" onClose={handleClose}>
        {t("addExistingUserToOrganization.title")}
      </DialogTitle>
      <DialogContent>
        <Stack sx={{ gap: 2, pt: 1 }}>
          <UserSearch
            onChange={setSelectedUser}
            onlyOrgAdminEligibleUsers={isOrgAdminUser}
            organizationIdToExclude={organizationId}
          />
          <Fade in={Boolean(selectedUser)}>
            <Stack sx={{ gap: 2 }}>
              <ReadonlyField
                label={t("addExistingUserToOrganization.nameLabel")}
                value={selectedUserName}
              />
              <ReadonlyField
                label={t("fields.email.title")}
                value={getDisplayValue(selectedUser?.email)}
              />
              <ReadonlyField
                label={t("fields.role.title")}
                value={selectedUserRoleLabel}
              />
            </Stack>
          </Fade>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          color="primary"
          disabled={addExistingUserToOrganizationIsPending}
          onClick={handleClose}
          variant="text"
        >
          {t("button.cancel")}
        </Button>
        <Button
          disabled={!selectedUser || addExistingUserToOrganizationIsPending}
          onClick={() => {
            void handleSubmit()
          }}
          variant="contained"
        >
          {t("button.save")}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
